import {
  OmsExportPreset,
  OmsPresetScope,
  OmsRecordFilter,
  OmsReport,
} from "../../lusca-search-api/generated-src";
import { OmsFilterCondition } from "../../hooks/useOmsFilterCriteria";
import { OmsColumnsDraft } from "./OMSColumns";
import { submittedCondition } from "./omsFilterEditing";

export const presetScopeLabel: Record<OmsPresetScope, string> = {
  filters: "Rows",
  columns: "Columns",
  both: "Rows and columns",
};

export type OmsExportSettings = {
  mode: "full" | "filtered";
  criteria: OmsFilterCondition[];
  operation: "and" | "or";
  columns: OmsColumnsDraft;
};

export type OmsConfiguration = OmsExportSettings & {
  appliedPreset?: OmsExportPreset;
  presetBaseline?: OmsExportSettings;
};

export function exportSettings(draft: OmsExportSettings): OmsExportSettings {
  return {
    mode: draft.mode,
    criteria: draft.criteria.map((filter) => ({
      ...filter,
      ...(filter.values ? { values: [...filter.values] } : {}),
    })),
    operation: draft.operation,
    columns: { ...draft.columns, names: [...draft.columns.names] },
  };
}

function filterSignature(filters: OmsRecordFilter[]) {
  return filters.map(({ column, operator, value, values }) => ({
    column,
    operator,
    ...(value !== undefined ? { value } : {}),
    ...(values !== undefined ? { values: [...values].sort() } : {}),
  }));
}

function rowsSignature(draft: OmsExportSettings) {
  return JSON.stringify({
    mode: draft.mode,
    operation: draft.mode === "full" ? "and" : draft.operation,
    filters: filterSignature(
      draft.mode === "full" ? [] : draft.criteria.map(submittedCondition)
    ),
  });
}

function columnsSignature(draft: OmsExportSettings) {
  return JSON.stringify({
    strategy: draft.columns.strategy,
    names: draft.columns.strategy === "selected" ? draft.columns.names : [],
  });
}

export function presetChangeAreas(
  preset: OmsExportPreset,
  draft: OmsConfiguration
) {
  const saved: OmsExportSettings = {
    mode: preset.rows === "all" ? "full" : "filtered",
    criteria: (preset.filters || []).map((filter, id) => ({ ...filter, id })),
    operation: preset.operation || "and",
    columns: {
      strategy: preset.columns?.strategy || "all",
      names: preset.columns?.names || [],
    },
  };
  // Unscoped settings are compared with the settings present just after applying
  // the preset. They are not assumed to be defaults or silently added on apply.
  const rowsBaseline =
    preset.scope === "columns" ? draft.presetBaseline : saved;
  const columnsBaseline =
    preset.scope === "filters" ? draft.presetBaseline : saved;
  return {
    rows: Boolean(
      rowsBaseline && rowsSignature(rowsBaseline) !== rowsSignature(draft)
    ),
    columns: Boolean(
      columnsBaseline &&
        columnsSignature(columnsBaseline) !== columnsSignature(draft)
    ),
  };
}

export function presetIsEdited(
  preset: OmsExportPreset,
  draft: OmsConfiguration
): boolean {
  const changed = presetChangeAreas(preset, draft);
  return changed.rows || changed.columns;
}

export function suggestedPresetScope(
  preset: OmsExportPreset,
  draft: OmsConfiguration
): OmsPresetScope {
  const changed = presetChangeAreas(preset, draft);
  return (preset.scope === "filters" && changed.columns) ||
    (preset.scope === "columns" && changed.rows)
    ? "both"
    : preset.scope;
}

export function applyPreset(
  preset: OmsExportPreset,
  draft: OmsConfiguration,
  report: OmsReport
): OmsConfiguration {
  const filters = (preset.filters || []).map((filter, id) => {
    const oldType = preset.schemaTypes[filter.column] || "string";
    const newType = report.columnTypes?.[filter.column] || "string";
    return {
      ...filter,
      id,
      ...(report.columns.includes(filter.column) && oldType !== newType
        ? {
            valueNotice: `This field now uses ${newType}. Review its condition and value.`,
          }
        : {}),
    };
  });
  const next: OmsConfiguration = {
    ...draft,
    ...(preset.scope !== "columns"
      ? {
          mode: preset.rows === "all" ? "full" : "filtered",
          criteria: preset.rows === "all" ? draft.criteria : filters,
          operation:
            preset.rows === "all" ? draft.operation : preset.operation || "and",
        }
      : {}),
    ...(preset.scope !== "filters"
      ? {
          columns: {
            strategy: preset.columns?.strategy || "selected",
            names:
              preset.columns?.strategy === "all"
                ? draft.columns.names
                : [...(preset.columns?.names || [])],
            touched: true,
            customOrder: true,
          },
        }
      : {}),
    appliedPreset: preset,
  };
  return { ...next, presetBaseline: exportSettings(next) };
}

export function restorePresetSettings(
  preset: OmsExportPreset,
  draft: OmsConfiguration,
  report: OmsReport
): OmsConfiguration {
  const baseline = exportSettings(draft.presetBaseline || draft);
  return {
    ...applyPreset(preset, { ...draft, ...baseline }, report),
    presetBaseline: baseline,
  };
}
